import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FullCalendarModule, FullCalendarComponent } from '@fullcalendar/angular';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CalendarOptions, EventInput, DateSelectArg, EventClickArg, EventApi, DayCellContentArg, EventChangeArg, EventMountArg, MoreLinkArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { AuthService } from '../../core/services/auth';
import { InteractionService, IInteraction } from '../../core/services/interaction.service';
import { CalendarService } from '../../core/services/calendar.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [CommonModule, FullCalendarModule, TagModule, ButtonModule, TooltipModule, ToastModule],
  providers: [MessageService],
  templateUrl: './calendar.html',
  styleUrl: './calendar.scss'
})
export class Calendar implements OnInit, OnDestroy {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  // User and interaction properties
  currentUserId: string = '';
  interactions: IInteraction[] = [];
  isLoading: boolean = false;
  showAllEvents: boolean = false;
  isSyncing: boolean = false;
  
  // Resize debounce timer
  private resizeTimer: any = null;
  private mutationObserver: MutationObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,today,next',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    weekends: true,
    events: [], // Events will be loaded dynamically
    selectable: true,
    selectMirror: true,
    dayMaxEvents: 3, // Limit to 3 events per day in month view
    select: (arg: DateSelectArg) => this.handleDateSelect(arg),
    dateClick: (arg: any) => this.handleDateClick(arg),
    eventClick: (arg: EventClickArg) => this.handleEventClick(arg),
    eventsSet: (events: EventApi[]) => this.handleEventsSet(events),
    eventChange: (arg: EventChangeArg) => this.handleEventChange(arg),
    dayCellContent: (arg: DayCellContentArg) => this.handleDayCellContent(arg),
    height: 'auto', // Use auto height for better mobile responsiveness
    dayMaxEventRows: false,
    moreLinkClick: (info) => this.handleMoreLinkClick(info),
    eventDisplay: 'block',
    // Time grid settings for week and day views
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    slotDuration: '01:00:00',
    slotLabelInterval: '01:00:00',
    // Scrolling settings
    scrollTime: '08:00:00',
    scrollTimeReset: false,
    // Enable scrolling
    handleWindowResize: true,
    // Mobile-friendly settings
    aspectRatio: 1.35, // Better aspect ratio for mobile
    expandRows: true, // Expand rows to fill available height
    // Custom event rendering for month view
    eventDidMount: (info) => this.handleEventDidMount(info),
    // Custom more link text
    moreLinkText: (num) => `+${num} more`,
    // Disable automatic time display since we include time in title
    displayEventTime: false,
    displayEventEnd: false
  };

  // Set initial selected date to July 16, 2025 to match the image
  selectedDate: Date = new Date(); // Set to current date by default
  selectedEvents: EventInput[] = [];
  allEvents: EventInput[] = [];
  currentView: string = 'dayGridMonth';

  constructor(
    private authService: AuthService,
    private interactionService: InteractionService,
    private calendarService: CalendarService,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.loadCurrentUser();
    this.setCurrentDateAsSelected();
    this.updateSelectedDateEvents();
    this.handleAuthCallback();
    
    // Set initial view based on screen size
    if (this.isMobile()) {
      this.currentView = 'timeGridDay';
      this.calendarOptions.initialView = 'timeGridDay';
      this.calendarOptions.headerToolbar = {
        ...this.calendarOptions.headerToolbar,
        right: 'timeGridDay,timeGridWeek,dayGridMonth'
      };
    }
    
    // Add window resize listener
    window.addEventListener('resize', () => this.onWindowResize());
    
    // Listen for sidebar toggle events
    document.addEventListener('sidebarToggle', () => {
      this.handleCalendarResize();
    });
    
    // Listen for any layout changes that might affect calendar size
    this.mutationObserver = new MutationObserver(() => {
      this.handleCalendarResize();
    });
    
    // Observe changes to the document body
    this.mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style']
    });

    // Use ResizeObserver to detect when the calendar container size changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleCalendarResize();
      });
      
      // Start observing the calendar container when it's available
      setTimeout(() => {
        const calendarElement = document.querySelector('.calendar-container');
        if (calendarElement) {
          this.resizeObserver?.observe(calendarElement);
        }
      }, 1000);
    }
  }

  loadCurrentUser() {
    const currentUser = this.authService.getLoggedInUser();
    if (currentUser && currentUser._id) {
      this.currentUserId = currentUser._id;
      this.loadUserInteractions();
    }
  }

  setCurrentDateAsSelected() {
    // Set the selected date to today's date
    this.selectedDate = new Date();
    
    // Also update the calendar to show today as selected
    setTimeout(() => {
      if (this.calendarComponent) {
        const calendarApi = this.calendarComponent.getApi();
        calendarApi.select(this.selectedDate);
      }
    }, 100);
  }

  handleAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const authStatus = urlParams.get('auth');
    const userEmail = urlParams.get('user');
    const errorMessage = urlParams.get('message');

    if (authStatus === 'success') {
      this.messageService.add({
        severity: 'success',
        summary: 'Authentication Completed!',
        detail: `Calendar access granted for ${userEmail || 'your account'}. You can now start with the calendar sync process!`,
        life: 5000
      });
      
      // Clear URL parameters
      this.router.navigate([], {
        relativeTo: this.router.routerState.root,
        queryParams: {}
      });
    } else if (authStatus === 'error') {
      this.messageService.add({
        severity: 'error',
        summary: 'Authorization Failed',
        detail: errorMessage || 'Failed to authorize calendar access. Please try again.',
        life: 5000
      });
      
      // Clear URL parameters
      this.router.navigate([], {
        relativeTo: this.router.routerState.root,
        queryParams: {}
      });
    }
  }

  loadUserInteractions() {
    if (!this.currentUserId) return;
    
    this.isLoading = true;
    this.interactionService.getInteractionsByUserId(this.currentUserId).subscribe({
      next: (interactions) => {
        this.interactions = interactions;
        this.transformInteractionsToEvents();
        this.updateSelectedDateEvents(); // Update selected date events after loading interactions
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading user interactions:', error);
        this.isLoading = false;
        // Fallback to hardcoded events if API fails
        this.loadFallbackEvents();
      }
    });
  }

  transformInteractionsToEvents() {
    const filteredInteractions = this.interactions.filter(interaction => {
      // Include interactions with status 'upcoming' or 'completed'
      return interaction.interactionStatus === 'upcoming' || interaction.interactionStatus === 'completed';
    });

    const events: EventInput[] = filteredInteractions.map(interaction => {
      // Parse the date string to get year, month, day in local time
      const dateStr = interaction.interactionDate.toString().split('T')[0]; // Get YYYY-MM-DD
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num));
      const [hours, minutes] = interaction.interactionTime.split(':').map(num => parseInt(num));
      
      // Create date in local time to avoid timezone issues
      const eventDate = new Date(year, month - 1, day, hours, minutes); // month is 0-indexed
      
      // Add 1 hour duration for the event
      const endDate = new Date(eventDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      console.log(`Interaction ${interaction._id}: Original date: ${interaction.interactionDate}, Parsed: ${dateStr}, Event date: ${eventDate.toDateString()} (${eventDate.toISOString()})`);
      
      // Set event colors based on status
      let backgroundColor = '#87CEEB'; // Default blue
      let borderColor = '#87CEEB';
      
      if (interaction.interactionStatus === 'completed') {
        backgroundColor = '#90EE90'; // Green
        borderColor = '#90EE90';
      } else if (interaction.interactionStatus === 'upcoming') {
        backgroundColor = '#DDA0DD'; // Purple
        borderColor = '#DDA0DD';
      }

      // Handle both string and populated object for reservationId
      const reservationData = typeof interaction.reservationId === 'string' 
        ? null 
        : interaction.reservationId;
      const businessName = reservationData?.customerId?.businessName || 'Unknown Customer';
      
      // Format time for display
      const timeStr = eventDate.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      // Create shorter title for calendar display with time and customer name
      const shortBusinessName = businessName.length > 12 ? businessName.substring(0, 12) + '...' : businessName;
      const interactionType = interaction.interactionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      return {
        title: `${timeStr} · ${shortBusinessName}`,
        start: eventDate.toISOString(),
        end: endDate.toISOString(),
        backgroundColor,
        borderColor,
        textColor: '#000',
        extendedProps: {
          type: 'interaction',
          status: interaction.interactionStatus,
          interactionId: interaction._id,
          reservationId: typeof interaction.reservationId === 'string' ? interaction.reservationId : interaction.reservationId._id,
          notes: interaction.interactionNotes,
          businessName: businessName,
          interactionType: interaction.interactionType,
          fullTitle: `${timeStr} - ${businessName} - ${interactionType}`,
          time: timeStr,
          duration: '1 hour'
        }
      };
    });

    // Update calendar events
    this.allEvents = events;
    this.calendarOptions = {
      ...this.calendarOptions,
      events: events
    };
    
    // Force calendar to refresh events if it's initialized
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(events);
    }
    
    this.updateSelectedDateEvents();
  }

  loadFallbackEvents() {
    // Keep the original hardcoded events as fallback
    const fallbackEvents: EventInput[] = [
      {
        title: 'Cust: A. Williams',
        start: '2025-07-08T10:00:00',
        end: '2025-07-08T11:00:00',
        backgroundColor: '#87CEEB',
        borderColor: '#87CEEB',
        textColor: '#000',
        extendedProps: {
          type: 'customer',
          status: 'upcoming'
        }
      },
      {
        title: 'Cust: B. Miller',
        start: '2025-07-10T10:00:00',
        end: '2025-07-10T11:00:00',
        backgroundColor: '#90EE90',
        borderColor: '#90EE90',
        textColor: '#000',
        extendedProps: {
          type: 'customer',
          status: 'upcoming'
        }
      }
    ];
    
    this.allEvents = fallbackEvents;
    this.calendarOptions = {
      ...this.calendarOptions,
      events: fallbackEvents
    };
    
    // Force calendar to refresh events if it's initialized
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(fallbackEvents);
    }
    
    // Update selected date events after loading fallback events
    this.updateSelectedDateEvents();
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    this.selectedDate = selectInfo.start;
    this.updateSelectedDateEvents();
    
    console.log('Date selected:', selectInfo.startStr);
  }

  handleDateClick(clickInfo: any) {
    this.selectedDate = clickInfo.date;
    this.updateSelectedDateEvents();
    
    console.log('Date clicked:', clickInfo.dateStr);
  }

  handleEventClick(clickInfo: EventClickArg) {
    const extendedProps = clickInfo.event.extendedProps;
    console.log('Event clicked:', clickInfo.event.title);
    
    if (extendedProps['type'] === 'interaction') {
      const interactionId = extendedProps['interactionId'];
      const reservationId = extendedProps['reservationId'];
      const status = extendedProps['status'];
      console.log('Interaction clicked:', { interactionId, reservationId, status });

      // If interaction is completed, redirect to reservation single view
      if (status === 'completed' && reservationId) {
        this.router.navigate(['/admin/view-reservation', reservationId]);
        return;
      }

      // For upcoming interactions, navigate to interaction update page
      if (interactionId) {
        this.router.navigate(['/admin/log-interaction/update', interactionId]);
        return;
      }


    }
  }

  handleEventChange(changeInfo: EventChangeArg) {
    console.log('Event changed:', changeInfo.event.title);
  }

  handleEventsSet(events: EventApi[]) {
    this.allEvents = events.map(event => ({
      title: event.title,
      start: event.start || undefined,
      end: event.end || undefined,
      backgroundColor: event.backgroundColor,
      borderColor: event.borderColor,
      textColor: event.textColor,
      extendedProps: event.extendedProps
    }));
    this.updateSelectedDateEvents();
  }

  handleDayCellContent(arg: DayCellContentArg) {
    return arg.dayNumberText;
  }

  handleEventDidMount(info: EventMountArg) {
    // This method is called when an event is mounted to the DOM
    // We can use this to customize event appearance or add additional functionality
    const event = info.event;
    const element = info.el;
    
    // Add custom styling or behavior if needed
    element.style.cursor = 'pointer';
    
    // Add tooltip functionality for calendar events
    const extendedProps = event.extendedProps;
    if (extendedProps) {
      const timeStr = this.getEventTime(event.start);
      const businessName = extendedProps['businessName'] || 'Unknown Customer';
      const interactionType = this.getInteractionTypeDisplay(extendedProps['interactionType']);
      const notes = extendedProps['notes'] || '';
      const status = this.getEventStatus({
        title: event.title,
        start: event.start || undefined,
        end: event.end || undefined,
        extendedProps: event.extendedProps
      });
      
      let tooltip = `📅 ${timeStr} - ${businessName}`;
      tooltip += `\n📋 Type: ${interactionType || 'N/A'}`;
      tooltip += `\n📊 Status: ${status.toUpperCase()}`;
      if (notes) {
        tooltip += `\n📝 Notes: ${notes}`;
      }
      
      element.title = tooltip;
    }
    
    // Add hover effect
    element.addEventListener('mouseenter', () => {
      element.style.opacity = '0.8';
      element.style.transform = 'scale(1.02)';
      element.style.transition = 'all 0.2s ease';
    });
    
    element.addEventListener('mouseleave', () => {
      element.style.opacity = '1';
      element.style.transform = 'scale(1)';
    });
  }

  handleMoreLinkClick(info: MoreLinkArg) {
    // Handle click on the "+X more" link
    const date = info.date;
    this.selectedDate = date;
    this.updateSelectedDateEvents();
    
    // Show a message about the additional events
    this.messageService.add({
      severity: 'info',
      summary: 'Additional Events',
      detail: `There are more events on ${date.toLocaleDateString()}. Check the sidebar for details.`,
      life: 3000
    });
    
    console.log('More link clicked for date:', date.toDateString());
  }

  updateSelectedDateEvents() {
    // Use local date comparison to avoid timezone issues
    const selectedYear = this.selectedDate.getFullYear();
    const selectedMonth = this.selectedDate.getMonth();
    const selectedDay = this.selectedDate.getDate();
    
    this.selectedEvents = this.allEvents.filter(event => {
      if (event.start) {
        let eventDate: Date;
        if (event.start instanceof Date) {
          eventDate = event.start;
        } else if (typeof event.start === 'string') {
          eventDate = new Date(event.start);
        } else {
          return false;
        }
        
        // Compare local date components
        return eventDate.getFullYear() === selectedYear &&
               eventDate.getMonth() === selectedMonth &&
               eventDate.getDate() === selectedDay;
      }
      return false;
    });
    

    this.showAllEvents = false;
    
    console.log(`Selected date: ${this.selectedDate.toDateString()}, Found ${this.selectedEvents.length} events`);
  }

  getEventStatus(event: EventInput): string {
    // Check if this is an interaction event with status
    if (event.extendedProps && event.extendedProps['status']) {
      return event.extendedProps['status'];
    }
    
    // Fallback logic for non-interaction events
    const eventDate = this.getEventDate(event.start);
    const now = new Date();
    
    if (eventDate < now) {
      return 'completed';
    }
    
    return 'upcoming';
  }

  private getEventDate(start: any): Date {
    if (start instanceof Date) {
      return start;
    } else if (typeof start === 'string') {
      return new Date(start);
    }
    return new Date();
  }

  getEventTime(start: any): string {
    if (!start) return '10:00 AM';
    
    let date: Date;
    if (start instanceof Date) {
      date = start;
    } else if (typeof start === 'string') {
      date = new Date(start);
    } else {
      return '10:00 AM';
    }
    
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  }

  getEventDateString(start: any): string {
    if (!start) return '';
    
    let date: Date;
    if (start instanceof Date) {
      date = start;
    } else if (typeof start === 'string') {
      date = new Date(start);
    } else {
      return '';
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  getStatusTagStyle(event: EventInput): any {
    const status = this.getEventStatus(event);
    
    if (status === 'upcoming') {
      return {
        'background-color': '#E5C6FF',
        'color': '#6B21A8'
      };
    } else if (status === 'completed') {
      return {
        'background-color': '#DCFCE7',
        'color': '#166534'
      };
    }
    
    // Default style
    return {
      'background-color': '#F3F4F6',
      'color': '#374151'
    };
  }

  getEventDisplayTitle(event: EventInput): string {
    // Use the full business name in sidebar
    if (event.extendedProps?.['businessName']) {
      const timeStr = this.getEventTime(event.start);
      return `${timeStr} - ${event.extendedProps['businessName']}`;
    }
    return event.title || '';
  }

  getEventTooltip(event: EventInput): string {
    const extendedProps = event.extendedProps;
    if (!extendedProps) return event.title || '';

    const timeStr = this.getEventTime(event.start);
    const businessName = extendedProps['businessName'] || 'Unknown Customer';
    const interactionType = this.getInteractionTypeDisplay(extendedProps['interactionType']);
    const notes = extendedProps['notes'] || '';
    const status = this.getEventStatus(event);
    
    let tooltip = `📅 ${timeStr} - ${businessName}`;
    tooltip += `\n📋 Type: ${interactionType || 'N/A'}`;
    tooltip += `\n📊 Status: ${status.toUpperCase()}`;
    if (notes) {
      tooltip += `\n📝 Notes: ${notes}`;
    }
    
    return tooltip;
  }

  getInteractionTypeDisplay(interactionType: string | undefined): string {
    if (!interactionType) return '';
    return interactionType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  }

  get displayedEvents(): EventInput[] {
    if (this.showAllEvents) {
      return this.selectedEvents;
    }
    return this.selectedEvents.slice(0, 3);
  }

  get hasMoreEvents(): boolean {
    return this.selectedEvents.length > 3;
  }

  viewAllEvents() {
    this.showAllEvents = true;
    console.log('View all events clicked');
  }

  onDateClick(date: Date) {
    this.selectedDate = date;
    this.updateSelectedDateEvents();
  }

  scheduleInteraction() {
    this.router.navigate(['/admin/log-interaction/create', this.selectedDate]);
  }

  syncWithGoogle() {
    if (!this.currentUserId) {
      console.error('No user ID available for syncing');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please log in to sync with Google Calendar'
      });
      return;
    }

    if (this.isSyncing) {
      return; 
    }

    console.log('Attempting to sync all interactions with Google Calendar...');
    
    // Try to sync directly first (user might already be authorized)
    this.performSync();
  }

  initiateGoogleAuth() {
    if (!this.currentUserId) {
      console.error('No user ID available for authorization');
      return;
    }

    console.log('Initiating Google Calendar OAuth2 authorization...');

    this.calendarService.initiateGoogleCalendarAuth().subscribe({
      next: (result) => {
        console.log('OAuth2 authorization URL generated:', result);

        const authUrl = result.data.authUrl;
        const authWindow = window.open(authUrl, 'google-auth', 'width=500,height=600');

        // Fallback if popup blocked: navigate current window
        if (!authWindow) {
          window.location.href = authUrl;
          return;
        }

        const checkClosed = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(checkClosed);
            // After authorization, try to sync
            this.performSync();
          }
        }, 1000);
      },
      error: (error) => {
        console.error('Error initiating Google Calendar auth:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Authorization Failed',
          detail: 'Failed to initiate Google Calendar authorization. Please try again later.',
          life: 5000
        });
      }
    });
  }



  private performSync() {
    if (!this.currentUserId) {
      console.error('No user ID available for syncing');
      return;
    }

    this.isSyncing = true;
    console.log('Syncing interactions with Google Calendar...');
    
    this.calendarService.syncInteractionsToGoogleCalendar(this.currentUserId).subscribe({
      next: (result) => {
        console.log('Google Calendar sync successful:', result);
        this.isSyncing = false;
        
        // Show detailed success message using PrimeNG MessageService
        const userEmail = (result.data as any).userEmail || 'your calendar';
        const createdCount = result.data.createdEvents || 0;
        const updatedCount = result.data.updatedEvents || 0;
        const failedCount = result.data.failedEvents || 0;
        
        let detail = `Successfully processed ${result.data.syncedEvents} out of ${result.data.totalInteractions} interactions to ${userEmail}!`;
        
        if (createdCount > 0) {
          detail += ` Created: ${createdCount} new events.`;
        }
        if (updatedCount > 0) {
          detail += ` Updated: ${updatedCount} existing events.`;
        }
        if (failedCount > 0) {
          detail += ` Failed: ${failedCount} events.`;
        }
        
        detail += ` No duplicates were created!`;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Sync Successful',
          detail: detail,
          life: 5000
        });
        
        // Log any failed syncs
        const failedSyncs = result.data.results.filter(r => r.status === 'failed');
        if (failedSyncs.length > 0) {
          console.warn('Some interactions failed to sync:', failedSyncs);
        }
      },
      error: (error) => {
        console.error('Error syncing with Google Calendar:', error);
        this.isSyncing = false;
        
        // Check if user needs to authorize
        const needsAuth = !!(error.error?.data?.needsAuth) ||
          /not authorized|authorize first|no token/i.test(error.error?.message || '');
        if (needsAuth) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Authorization Required',
            detail: 'You need to authorize Google Calendar access first. Click the sync button to start authorization.',
            life: 5000
          });
          // Automatically trigger authorization
          setTimeout(() => {
            this.initiateGoogleAuth();
          }, 800);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Sync Failed',
            detail: `Failed to sync with Google Calendar: ${error.error?.message || error.message}`,
            life: 5000
          });
        }
      }
    });
  }

  syncWithOutlook() {
    if (!this.currentUserId) {
      console.error('No user ID available for syncing');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please log in to sync with Outlook Calendar'
      });
      return;
    }

    if (this.isSyncing) {
      return; 
    }

    console.log('Attempting to sync all interactions with Outlook Calendar...');
    
    // Try to sync directly first (user might already be authorized)
    this.performOutlookSync();
  }

  initiateOutlookAuth() {
    if (!this.currentUserId) {
      console.error('No user ID available for authorization');
      return;
    }

    console.log('Initiating Outlook Calendar OAuth2 authorization...');

    this.calendarService.initiateOutlookCalendarAuth().subscribe({
      next: (result) => {
        console.log('OAuth2 authorization URL generated:', result);

        const authUrl = result.data.authUrl;
        const authWindow = window.open(authUrl, 'outlook-auth', 'width=500,height=600');

        // Fallback if popup blocked: navigate current window
        if (!authWindow) {
          window.location.href = authUrl;
          return;
        }

        const checkClosed = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(checkClosed);
            // After authorization, try to sync
            this.performOutlookSync();
          }
        }, 1000);
      },
      error: (error) => {
        console.error('Error initiating Outlook Calendar auth:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Authorization Failed',
          detail: 'Failed to initiate Outlook Calendar authorization. Please try again later.',
          life: 5000
        });
      }
    });
  }

  private performOutlookSync() {
    if (!this.currentUserId) {
      console.error('No user ID available for syncing');
      return;
    }

    this.isSyncing = true;
    console.log('Syncing interactions with Outlook Calendar...');
    
    this.calendarService.syncInteractionsToOutlookCalendar(this.currentUserId).subscribe({
      next: (result) => {
        console.log('Outlook Calendar sync successful:', result);
        this.isSyncing = false;
        
        // Show detailed success message using PrimeNG MessageService
        const userEmail = (result.data as any).userEmail || 'your calendar';
        const createdCount = result.data.createdEvents || 0;
        const updatedCount = result.data.updatedEvents || 0;
        const failedCount = result.data.failedEvents || 0;
        
        let detail = `Successfully processed ${result.data.syncedEvents} out of ${result.data.totalInteractions} interactions to ${userEmail}!`;
        
        if (createdCount > 0) {
          detail += ` Created: ${createdCount} new events.`;
        }
        if (updatedCount > 0) {
          detail += ` Updated: ${updatedCount} existing events.`;
        }
        if (failedCount > 0) {
          detail += ` Failed: ${failedCount} events.`;
        }
        
        detail += ` No duplicates were created!`;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Sync Successful',
          detail: detail,
          life: 5000
        });
        
        // Log any failed syncs
        const failedSyncs = result.data.results.filter((r: any) => r.status === 'failed');
        if (failedSyncs.length > 0) {
          console.warn('Some interactions failed to sync:', failedSyncs);
        }
      },
      error: (error) => {
        console.error('Error syncing with Outlook Calendar:', error);
        this.isSyncing = false;
        
        // Check if user needs to authorize
        const needsAuth = !!(error.error?.data?.needsAuth) ||
          /not authorized|authorize first|no token/i.test(error.error?.message || '');
        if (needsAuth) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Authorization Required',
            detail: 'You need to authorize Outlook Calendar access first. Click the sync button to start authorization.',
            life: 5000
          });
          // Automatically trigger authorization
          setTimeout(() => {
            this.initiateOutlookAuth();
          }, 800);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Sync Failed',
            detail: `Failed to sync with Outlook Calendar: ${error.error?.message || error.message}`,
            life: 5000
          });
        }
      }
    });
  }

  getCalendarApi() {
    return this.calendarComponent.getApi();
  }

  goToToday() {
    const calendarApi = this.getCalendarApi();
    calendarApi.today();
    this.selectedDate = new Date();
    this.updateSelectedDateEvents();
  }

  goToPreviousMonth() {
    const calendarApi = this.getCalendarApi();
    calendarApi.prev();
  }

  goToNextMonth() {
    const calendarApi = this.getCalendarApi();
    calendarApi.next();
  }

  changeView(view: string) {
    this.currentView = view;
    const calendarApi = this.getCalendarApi();
    calendarApi.changeView(view);
    
    // Refresh events after view change to ensure they display correctly
    setTimeout(() => {
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(this.allEvents);
    }, 100);
  }

  addEvent() {
    const newEvent: EventInput = {
      title: 'New Event',
      start: this.selectedDate.toISOString(),
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      textColor: '#ffffff'
    };
    
    this.allEvents.push(newEvent);
    this.updateSelectedDateEvents();
    
    console.log('Add event clicked');
  }

  getCurrentMonth() {
    return new Date().toLocaleString('en-US', { month: 'long' });
  }

  getCurrentYear() {
    return new Date().getFullYear();
  }

  private isMobile(): boolean {
    return window.innerWidth < 768; // Adjust breakpoint as needed
  }

  private onWindowResize() {
    if (this.isMobile()) {
      this.currentView = 'timeGridDay';
      this.calendarOptions.initialView = 'timeGridDay';
      this.calendarOptions.headerToolbar = {
        ...this.calendarOptions.headerToolbar,
        right: 'timeGridDay,timeGridWeek,dayGridMonth'
      };
    } else {
      this.currentView = 'dayGridMonth';
      this.calendarOptions.initialView = 'dayGridMonth';
      this.calendarOptions.headerToolbar = {
        ...this.calendarOptions.headerToolbar,
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      };
    }
    
    // Force calendar to resize and recalculate dimensions
    this.handleCalendarResize();
  }

  private handleCalendarResize() {
    // Clear existing timer
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // Debounce resize calls to prevent excessive updates
    this.resizeTimer = setTimeout(() => {
      if (this.calendarComponent) {
        const calendarApi = this.calendarComponent.getApi();
        calendarApi.updateSize();
      }
    }, 150);
  }

  // Public method that can be called from parent components
  public resizeCalendar() {
    this.handleCalendarResize();
  }

  ngOnDestroy() {
    // Clean up timers
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // Clean up observers
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Remove event listeners
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('sidebarToggle', this.handleCalendarResize);
  }
}